/* eslint-disable react/no-unknown-property */
import { useContext, useEffect } from 'react';
import { DateTime } from 'luxon';
import {
	Link,
	useHref,
	useLocation,
	useNavigate,
	useParams,
} from 'react-router-dom';

import styles from './Preview.module.css';

import config from '../../config';

import { Icon } from '../../components/icon/Icon';
import { useBackUrl } from '../../App.hooks';
import { useGetIssue } from './Preview.hooks';
import { Issue, Article, NavbarIconLogo, USER_ROLE } from '../../constants';
import { Loading } from '../../components/loading/Loading';
import { useTrackingPageView } from '../../tracking/Tracking.hooks';
import {
	AUTH_KEY,
	IS_SUBSCRIBED_KEY,
} from '../../views/home/auth/Authentication.hooks';
import { AuthCognito } from 'config.type';

export const Preview = () => {
	return (
		<div className={styles['container']}>
			<View />
		</div>
	);
};

function View() {
	return (
		<div className={styles['view']}>
			<Topbar />
			<Content />
		</div>
	);
}

export function Topbar(props?: BackBtnProps) {
	return (
		<div className={styles['topbar']}>
			<div>
				<BackButton {...props} />
			</div>
			<div className={styles['title']}>
				<NavbarIconLogo />
			</div>
			<div></div>
		</div>
	);
}

export function BackButton(props?: BackBtnProps) {
	const url = useBackUrl();
	return (
		<button id="back-button" type="button" title="Back">
			<Link to={props?.url ? props.url : url}>
				<Icon id={'back-btn'} name={'arrow-left'} color={'#333'} />
			</Link>
		</button>
	);
}

interface BackBtnProps {
	url?: string;
}

function Content() {
	const { id } = useParams();
	const location = useLocation();
	const { issue, isLoading } = useGetIssue(`${id}`);

	useTrackingPageView({
		title: issue?.name,
		location: location.pathname,
	});
	useEffect(() => {
		if (issue?.name) {
			document.title = issue.name;
		}
	}, [issue]);

	if (!id) {
		return null;
	}

	if (isLoading || issue === null) {
		return <Loading />;
	}

	console.log(`ISSUE: `, issue);

	return (
		<div className={styles['content']}>
			<div className={styles['cover']}>
				<Cover {...issue} />
				<Details {...issue} />
			</div>
		</div>
	);
}

function Cover(issue: Issue) {
	const { name, cover, slug, id } = issue;
	const url = `/issues/${slug ? slug : id}`;
	const from = window.location.pathname;
	const contentProtection = config?.app?.contentProtection;
	return (
		<section>
			<Link to={url} state={{ from }} className={styles['image']}>
				{contentProtection ? (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={500}
						height={667}
						className="content-protection"
						onContextMenu={(e) => e.preventDefault()}
					/>
				) : (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={500}
						height={667}
					/>
				)}
			</Link>
			<div className={styles['title']}>
				<h1>{name}</h1>
				<hr />
			</div>
		</section>
	);
}

function Details(issue: Issue) {
	const { id, slug, articles } = issue;
	return (
		<section className={styles['details']}>
			<Description {...issue} />
			<hr />
			<Toc id={slug ? slug : id} articles={articles} />
		</section>
	);
}

function Description(issue: Issue) {
	const { name, releaseDate, description } = issue;

	return (
		<div>
			{name && <h1>{name}</h1>}
			{releaseDate && (
				<h2>
					Published on{' '}
					{DateTime.fromISO(releaseDate).toFormat('dd LLLL yyyy')}
				</h2>
			)}
			{description && <p>{description}</p>}
			<LoginBtn />
			<SubscribedBtn />
			<ReadNowBtn {...issue} />
		</div>
	);
}

function LoginBtn() {
	const basename = useHref('/callback');
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	if (isAuthenticated) {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/login?', redirectSections].join(
		'',
	);

	return (
		<button
			onClick={() => {
				location.href = redirectUrl;
			}}
		>
			Login to read
		</button>
	);
}

function SubscribedBtn() {
	const navigate = useNavigate();
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (isSubscribed || !isAuthenticated) {
		return null;
	}
	return (
		<button
			onClick={() => {
				navigate('/subscriptions');
			}}
		>
			Subscribe
		</button>
	);
}

function ReadNowBtn(issue: Issue) {
	const navigate = useNavigate();
	const { id, slug } = issue;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const btnComponent = (
		<button
			className={styles['read-now-btn']}
			onClick={() => {
				navigate(`/issues/${slug ? slug : id}`);
			}}
		>
			Read Now
		</button>
	);

	if (!config.app.auth.enabled) {
		return btnComponent;
	}

	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (!isAdmin) {
		if (!isAuthenticated || !isSubscribed) {
			return null;
		}
	}

	return btnComponent;
}

function Toc({ id, articles }: TocProps) {
	if (!articles) {
		return null;
	}

	const filteredArticles = articles
		? articles.filter(onlyIncludedToc).sort(sortByIndex)
		: [];
	const from = window.location.pathname;
	return (
		<section className={styles['toc-container']}>
			<h1>Contents</h1>

			<ul className={styles['toc']}>
				{filteredArticles.map((article: Article) => (
					<li
						key={article.id}
						article-index={article.index}
						is-feature={article.isFeatured ? `` : undefined}
					>
						<Link
							to={`/issues/${id}/${article.slug || article.id}`}
							state={{ from }}
						>
							<h3>{article.name['en']}</h3>
						</Link>
					</li>
				))}
			</ul>
		</section>
	);
}

interface TocProps {
	id: string;
	articles?: Array<Article>;
}

function sortByIndex(a: Article, b: Article): number {
	return a.index - b.index;
}

/*Exclude articles that are excluded from being display in Toc*/
function onlyIncludedToc(article: any) {
	if (article.role === 'advert') {
		return false;
	}
	return (
		article.channel === undefined ||
		article.channel === null ||
		article?.channel?.toc === 'included'
	);
}
